import React, { useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { CHARACTERS, CATEGORIES, GENDERS, SURPRISE_CHARACTER, SURPRISE_ID, SURPRISE_POOL } from '../data/characters';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Check, Users, User, Shuffle, Sparkles } from 'lucide-react';
import { Input } from '../ui/input';

export default function CharacterSelector({ selectedCharacter, onSelect, characters }) {
  const { language, isRTL } = useLanguage();
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeGender, setActiveGender] = useState('male');
  const [searchQuery, setSearchQuery] = useState('');

  const sourceCharacters = (characters && characters.length > 0) ? characters : CHARACTERS;

  const filteredCharacters = sourceCharacters.filter(char => {
    const matchesCategory = activeCategory === 'all' || char.category === activeCategory;
    const matchesGender = activeGender === 'all' || char.gender === activeGender || char.isCustom || char.gender === 'all' || char.gender === 'neutral';
    const matchesSearch = searchQuery === '' || 
      char.name_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      char.name_he.includes(searchQuery);
    return matchesCategory && matchesGender && matchesSearch;
  });

  // Group characters by category for display
  const groupedCharacters = CATEGORIES.filter(cat => cat.id !== 'all').reduce((acc, category) => {
    const chars = filteredCharacters.filter(char => char.category === category.id);
    if (chars.length > 0) {
      acc.push({ category, characters: chars });
    }
    return acc;
  }, []);

  const categorizedIds = new Set(
    groupedCharacters.flatMap((group) => group.characters.map((char) => char.id))
  );

  const uncategorized = filteredCharacters.filter(
    (char) => !categorizedIds.has(char.id)
  );

  if (uncategorized.length > 0) {
    groupedCharacters.push({
      category: {
        id: 'other',
        name_en: 'Other',
        name_he: '    ',
      },
      characters: uncategorized,
    });
  }

  return (
    <div className="space-y-6">
      {/* Gender Toggle */}
      <div className="flex justify-center gap-2 p-1 bg-slate-800/50 rounded-xl w-fit mx-auto">
        {GENDERS.map((gender) => (
          <button
            key={gender.id}
            onClick={() => setActiveGender(gender.id)}
            className={`px-6 py-3 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeGender === gender.id
                ? 'bg-gradient-to-r from-violet-600 to-pink-600 text-white shadow-lg'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
            }`}
          >
            {gender.id === 'male' && <User className="w-4 h-4" />}
            {gender.id === 'female' && <Users className="w-4 h-4" />}
            {language === 'he' ? gender.name_he : gender.name_en}
          </button>
        ))}
      </div>

      {/* Surprise Me — pinned above the grid so it reads as its own thing, not
          just another costume in the list. */}
      <button
        onClick={() => onSelect(SURPRISE_CHARACTER)}
        className={`w-full rounded-2xl p-4 flex items-center gap-4 transition-all border-2 ${
          selectedCharacter?.id === SURPRISE_ID
            ? 'border-amber-400 bg-gradient-to-r from-amber-500/20 to-pink-500/20 shadow-lg shadow-amber-500/10'
            : 'border-slate-700 bg-gradient-to-r from-violet-600/10 to-pink-600/10 hover:border-amber-400/60'
        }`}
      >
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center shrink-0">
          <Shuffle className="w-7 h-7 text-white" />
        </div>
        <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
          <p className="text-white font-bold text-lg flex items-center gap-2">
            {language === 'he' ? 'תפתיע אותי' : 'Surprise Me'}
            <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
          </p>
          <p className="text-sm text-slate-400">
            {language === 'he'
              ? `דמות אקראית מתוך ${SURPRISE_POOL.length} — כולל דמויות שלא מופיעות ברשימה`
              : `A random character from ${SURPRISE_POOL.length} — including ones not in the list`}
          </p>
        </div>
        {selectedCharacter?.id === SURPRISE_ID && (
          <Check className="w-6 h-6 text-amber-400 shrink-0" />
        )}
      </button>

      {/* Search */}
      <div className="relative">
        <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 ${isRTL ? 'right-4' : 'left-4'}`} />
        <Input
          type="text"
          placeholder={language === 'he' ? 'חפש דמות...' : 'Search character...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 h-12 ${isRTL ? 'pr-12 text-right' : 'pl-12'}`}
        />
      </div>

      {/* Categories - Horizontal Scroll */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setActiveCategory(cat.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              activeCategory === cat.id
                ? 'bg-violet-600 text-white'
                : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
          >
            {language === 'he' ? cat.name_he : cat.name_en}
          </button>
        ))}
        {uncategorized.length > 0 && (
          <button
            onClick={() => setActiveCategory('custom')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
              activeCategory === 'custom'
                ? 'bg-violet-600 text-white'
                : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700'
            }`}
          >
            {language === 'he' ? 'דמויות אישיות' : 'Custom'}
          </button>
        )}
      </div>

      {/* Characters by Category */}
      <div className="max-h-[55vh] overflow-y-auto custom-scrollbar space-y-8 pe-2">
        {activeCategory === 'all' ? (
          // Show grouped by category
          groupedCharacters.map(({ category, characters }) => (
            <div key={category.id}>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                {language === 'he' ? category.name_he : category.name_en}
                <span className="text-sm text-slate-500">({characters.length})</span>
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {characters.map((char) => (
                  <CharacterCard 
                    key={char.id} 
                    char={char} 
                    selected={selectedCharacter?.id === char.id}
                    onSelect={onSelect}
                    language={language}
                  />
                ))}
              </div>
            </div>
          ))
        ) : (
          // Show flat list for specific category
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <AnimatePresence mode="popLayout">
              {filteredCharacters.map((char) => (
                <CharacterCard 
                  key={char.id} 
                  char={char} 
                  selected={selectedCharacter?.id === char.id}
                  onSelect={onSelect}
                  language={language}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {filteredCharacters.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500">
              {language === 'he' ? 'לא נמצאו דמויות' : 'No characters found'}
            </p>
          </div>
        )}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(139, 92, 246, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(139, 92, 246, 0.7);
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}

function CharacterCard({ char, selected, onSelect, language }) {
  return (
    <motion.button
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      onClick={() => onSelect(char)}
      className={`relative group rounded-xl overflow-hidden aspect-[3/4] ${
        selected
          ? 'ring-2 ring-violet-500 ring-offset-2 ring-offset-slate-950'
          : ''
      }`}
    >
      <img
        src={char.image_url}
        alt={language === 'he' ? char.name_he : char.name_en}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
      
      {/* Selected Check */}
      {selected && (
        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center">
          <Check className="w-5 h-5 text-white" />
        </div>
      )}

      {/* Name */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <span className="text-sm font-medium text-white">
          {language === 'he' ? char.name_he : char.name_en}
        </span>
      </div>
    </motion.button>
  );
}
